import { Button, Group, Modal, Stack, Text, useMantineTheme } from '@mantine/core'
import { IconPlus, IconShoppingCart } from '@tabler/icons'
import { useRouter } from 'next/router'
import { useCallback, useEffect, useRef, useState } from 'react'
import { useSWRConfig } from 'swr'

import { LoginSignupModalForm } from '@/containers/forms'
import { useStudent } from '@/lib/hooks'
import {
  api,
  getProductInfo,
  getSession,
  notificationHandler,
  productOptions,
  removeSession,
  setSession
} from '@/lib/utils'
import { useAuth } from '@/providers/AuthProvider'
import { useNav } from '@/providers/NavProvider'
import {
  BasketProductOption as BasketProductOptionType,
  Price as PriceType,
  Product as ProductType,
  ProductOption as ProductOptionType,
  Showcase as ShowcaseType,
  Site as SiteType,
  Store as StoreType,
  StudentChannelProfile as StudentChannelProfileType
} from '@/types'

interface Props {
  siteData?: SiteType
  store?: StoreType
  showcase?: ShowcaseType
  product: ProductType
  selectedOptions?: any
  addToCart: boolean
  addToCartLabel?: string
  addToCartProps?: any
  buyNow: boolean
  buyNowLabel?: string
  buyNowProps?: any
  continueBuying?: boolean
  handleClosePaymentDrawer?: () => void
  handleCloseView?: () => void
}

export default function BuyButton({
  siteData,
  store,
  showcase,
  product,
  selectedOptions = [],
  addToCart = true,
  addToCartLabel,
  addToCartProps = {},
  buyNow = true,
  buyNowLabel,
  buyNowProps = {},
  continueBuying = true,
  handleClosePaymentDrawer,
  handleCloseView
}: Props) {
  // Hooks
  const router = useRouter()
  const theme = useMantineTheme()
  const { isAuthenticated } = useAuth() || {}
  const { setBasketDrawerOpen, setStudentModalOpenFromBuyButton } = useNav()
  const { mutate: mutateGlobal } = useSWRConfig()
  const { studentData } = useStudent()

  // Constants
  const {
    site: siteSlug,
    channel: channelSlug,
    store: storeSlug,
    showcase: showcaseSlug,
    collection: collectionSlug
  } = router.query || {}

  const { clean } = productOptions || {}
  const { uid: productUid, hasOptions } = product || {}
  const { price } = getProductInfo(product) || {}
  const hideSignUp = !siteData?.isSignupAllowed || siteData?.privacy === 'private'

  const addButtonRef = useRef(null)
  const buyButtonRef = useRef(null)

  const continueBuyingUrl = `/${channelSlug}/${storeSlug}/${showcaseSlug}${
    collectionSlug ? `?collection=${collectionSlug}` : ''
  }`

  const sessionStudentUid = getSession('stuid')

  const currentChannelStudentChannelProfile = studentData?.studentChannelProfiles?.find(
    (studentChannelProfile: StudentChannelProfileType) =>
      studentChannelProfile?.channelProfile?.channel?.slug === channelSlug
  )

  // States
  const [loading, setLoading] = useState(null)
  const [openedModal, setOpenedModal] = useState(false)
  const [confirmModal, setConfirmModal] = useState(null)
  const [canAddToCart, setCanAddToCart] = useState<boolean>(!hasOptions)

  // Actions
  const handleQuantity = (price?: PriceType) => {
    if (price?.minimalSellingQuantity) {
      return price?.minimalSellingQuantity
    }
    return 1
  }

  const addProductToBasket = useCallback(
    async (productUid: string, options: BasketProductOptionType[], price?: PriceType) => {
      const quantity = handleQuantity(price)
      const response = await api.post(`/${siteSlug}/basket/add/`, {
        product: productUid,
        ...(siteData?.beneficiary !== 'shopper' ? { student: sessionStudentUid } : {}),
        showcase: showcase?.uid,
        quantity,
        options: clean(options)
      })
      return response
    },
    [showcase?.uid, siteSlug, sessionStudentUid, siteData?.beneficiary, clean]
  )

  const checkAddOptionsToCart = useCallback(
    (values: any) => {
      setCanAddToCart(false)
      const requiredOptions = product?.options
        ?.map((option: ProductOptionType) => (option.isRequired ? option.code : null))
        ?.filter((value: string | null) => value)
      const filledOptions = Object.keys(values).filter(
        (key: string) =>
          requiredOptions?.includes(key) &&
          (values[key] instanceof Array ? values[key].length : values[key])
      )
      const unfilledOptions = requiredOptions?.filter(
        (key: string | null) => key && !filledOptions.includes(key)
      )
      if (unfilledOptions?.length === 0) {
        setCanAddToCart(true)
      }
    },
    [product?.options]
  )

  const onAddConfirm = useCallback(
    async (buttonRef: any, cleanBasket: boolean = false) => {
      setLoading(buttonRef)
      if (isAuthenticated) {
        if (sessionStudentUid || siteData?.beneficiary === 'shopper') {
          removeSession('buttonRef')
          removeSession('productUid')
          try {
            if (cleanBasket) {
              await api.post(`/${siteSlug}/basket/clean/`, { studentUid: studentData?.uid })
            }
            await addProductToBasket(productUid, selectedOptions, price)
            if (buttonRef === addButtonRef || buttonRef === 'add') {
              mutateGlobal(`/${siteSlug}/basket/`)
              notificationHandler({
                variant: 'success',
                message: 'Produto adicionado ao carrinho!'
              })
              if (continueBuying) router.push(continueBuyingUrl)
            } else {
              setBasketDrawerOpen?.(true)
            }
            removeSession('modalAction')
            setConfirmModal(null)
            handleClosePaymentDrawer?.()
            handleCloseView?.()
          } catch (error: any) {
            const [errors] = error?.response?.data?.nonFieldErrors || []
            notificationHandler({
              variant: 'error',
              message: errors || 'Ocorreu um erro ao adicionar o produto no carrinho.'
            })
          }
        } else {
          setSession('buttonRef', buttonRef === addButtonRef ? 'add' : 'buy')
          setSession('productUid', productUid)
          setStudentModalOpenFromBuyButton?.(true)
        }
      } else {
        // Show login/register popup
        setSession('buttonRef', buttonRef === addButtonRef ? 'add' : 'buy')
        setSession('productUid', productUid)
        setOpenedModal(true)
      }
      setLoading(null)
    },
    [
      isAuthenticated,
      sessionStudentUid,
      siteData?.beneficiary,
      studentData?.uid,
      addProductToBasket,
      productUid,
      selectedOptions,
      price,
      mutateGlobal,
      siteSlug,
      handleClosePaymentDrawer,
      router,
      continueBuyingUrl,
      setBasketDrawerOpen,
      setStudentModalOpenFromBuyButton,
      continueBuying
    ]
  )

  const handleAdd = useCallback(
    (buttonRef: any) => {
      const shouldConfirmStudentEnrollmentChange =
        !!channelSlug &&
        !!showcaseSlug &&
        !!studentData &&
        !!currentChannelStudentChannelProfile &&
        !!currentChannelStudentChannelProfile?.showcase &&
        !!currentChannelStudentChannelProfile.showcase?.uid &&
        currentChannelStudentChannelProfile.showcase.uid !== showcase?.uid

      if (shouldConfirmStudentEnrollmentChange) setConfirmModal(buttonRef)
      else onAddConfirm(buttonRef)
    },
    [
      channelSlug,
      showcaseSlug,
      showcase?.uid,
      studentData,
      currentChannelStudentChannelProfile,
      onAddConfirm
    ]
  )

  const handleAddOrBuy = useCallback(async () => {
    const sessionButtonRef = getSession('buttonRef')
    const sessionProductUid = getSession('productUid')
    if (sessionButtonRef && sessionProductUid === productUid) {
      await handleAdd(sessionButtonRef)
    }
  }, [handleAdd, productUid])

  const handleCloseConfirmModal = () => {
    setConfirmModal(null)
    removeSession('buttonRef')
    removeSession('productUid')
  }

  // Effects
  useEffect(() => {
    if (hasOptions) {
      checkAddOptionsToCart(selectedOptions)
    }
  }, [hasOptions, selectedOptions, checkAddOptionsToCart])

  useEffect(() => {
    async function runModalFallback() {
      const sessionModalAction = getSession('modalAction')
      if (sessionModalAction) {
        if (!sessionStudentUid && siteData?.beneficiary !== 'shopper') {
          setStudentModalOpenFromBuyButton?.(true)
        } else if (siteData?.beneficiary === 'shopper') {
          await handleAddOrBuy()
        }
      }
    }
    runModalFallback()
  }, [handleAddOrBuy, setStudentModalOpenFromBuyButton, siteData?.beneficiary, sessionStudentUid])

  useEffect(() => {
    async function runFallbackFunction() {
      if (sessionStudentUid) {
        await handleAddOrBuy()
      }
    }
    runFallbackFunction()
  }, [sessionStudentUid, handleAddOrBuy])

  return (
    <>
      <Stack spacing="xs">
        {!!addToCart && (
          <Button
            loading={loading === addButtonRef}
            variant="outline"
            onClick={() => handleAdd(addButtonRef)}
            disabled={!canAddToCart}
            leftIcon={<IconPlus />}
            fullWidth
            ref={addButtonRef}
            {...addToCartProps}>
            {addToCartLabel || 'Adicionar ao carrinho'}
          </Button>
        )}
        {!!buyNow && (
          <Button
            fullWidth
            loading={loading === buyButtonRef}
            onClick={() => handleAdd(buyButtonRef)}
            disabled={!canAddToCart}
            leftIcon={<IconShoppingCart />}
            ref={buyButtonRef}
            {...buyNowProps}>
            {buyNowLabel || 'Comprar agora'}
          </Button>
        )}
      </Stack>
      <Modal
        centered
        size="auto"
        title={<Text weight="bold">Confirmar alteração de vitrine?</Text>}
        opened={!!confirmModal}
        onClose={handleCloseConfirmModal}>
        <Text mb="xs">
          Você está tentando adicionar um produto da vitrine{' '}
          <em>
            <strong>
              {store?.grade?.parent?.name}: {store?.displayName} - {showcase?.displayName} (
              {showcase?.cycle})
            </strong>
          </em>{' '}
          para o aluno{' '}
          <strong>
            {studentData?.firstName} {studentData?.lastName}
          </strong>
          .
        </Text>
        <Text mb="xs">
          No entanto, o aluno{' '}
          <strong>
            {studentData?.firstName} {studentData?.lastName}
          </strong>{' '}
          já está matriculado em{' '}
          <em>
            <strong>
              {currentChannelStudentChannelProfile?.showcase?.store?.grade?.parent?.name}:{' '}
              {currentChannelStudentChannelProfile?.showcase?.store?.displayName} -{' '}
              {currentChannelStudentChannelProfile?.showcase?.displayName} (
              {currentChannelStudentChannelProfile?.showcase?.cycle})
            </strong>
          </em>
          .
        </Text>
        <Text mb="xl">
          Deseja atualizar a matrícula de{' '}
          <strong>
            {studentData?.firstName} {studentData?.lastName}
          </strong>{' '}
          para{' '}
          <em>
            <strong>
              {store?.grade?.parent?.name}: {store?.displayName} - {showcase?.displayName} (
              {showcase?.cycle})
            </strong>
          </em>
          ?
        </Text>
        <Group position="apart">
          <Button color="red" disabled={loading === confirmModal} onClick={handleCloseConfirmModal}>
            Cancelar
          </Button>
          <Button
            color={theme.colors[theme.primaryColor][7]}
            loading={loading === confirmModal}
            onClick={() => onAddConfirm(confirmModal, false)}>
            Confirmar
          </Button>
        </Group>
      </Modal>
      <LoginSignupModalForm
        setOpened={setOpenedModal}
        opened={openedModal}
        hideSignUp={hideSignUp}
      />
    </>
  )
}
